/* Variant picker — dynamic availability filtering
   Toggled by `data-hide-dynamic="true"` on <variant-selects>.
   Reads the variants JSON emitted by snippets/product-variant-picker.liquid
   and hides option values that cannot combine with the current selection.
   When a currently-selected value becomes unreachable, the first available
   value in that option is selected and a 'change' event is dispatched so the
   existing product-info.js variant-swap pipeline picks it up.

   Debug logging:
   - Add `data-debug="true"` to <variant-selects> for verbose logs.
   - Or set window.__variantPickerDebug = true before the script runs.
*/
(function () {
  var HIDDEN_CLASS = 'variant-picker-hidden';
  var LOG_PREFIX = '[variant-picker]';
  var EVENT_NAME = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.optionValueSelectionChange)
    ? PUB_SUB_EVENTS.optionValueSelectionChange
    : 'option-value-selection-change';

  function globalDebug() {
    return typeof window !== 'undefined' && window.__variantPickerDebug === true;
  }

  function makeLogger(picker) {
    // Logs are unconditional during the debug phase. Set window.__variantPickerQuiet = true to silence.
    return function () {
      if (typeof window !== 'undefined' && window.__variantPickerQuiet === true) return;
      var args = Array.prototype.slice.call(arguments);
      args.unshift(LOG_PREFIX);
      // eslint-disable-next-line no-console
      console.log.apply(console, args);
    };
  }

  var SETUP_FLAG = '__variantPickerSetup';
  var BUSY_CLASS = 'variant-picker-busy';
  var BUSY_TIMEOUT_MS = 4000;

  /* New-product template value sort.
     Reorders option-value DOM nodes inside each <variant-selects> so values
     containing one of the priority phrases (case-insensitive, word-boundary)
     render first in the listed order. Unmatched values keep admin order via
     stable sort. Runs only when <body new-product> is present. */
  var NEW_PRODUCT_VALUE_ORDER = ['1 month', '2 month', '3 month', '4 month', '6 month', '105 Days Dose'];
  var SORTED_FLAG = '__variantValuesSorted';
  function escapeRegex(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  var NEW_PRODUCT_VALUE_REGEXES = NEW_PRODUCT_VALUE_ORDER.map(function (phrase) {
    return new RegExp('\\b' + escapeRegex(phrase) + '\\b', 'i');
  });
  function normLabel(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
  }
  function rankLabel(text) {
    var key = normLabel(text);
    for (var i = 0; i < NEW_PRODUCT_VALUE_REGEXES.length; i++) {
      if (NEW_PRODUCT_VALUE_REGEXES[i].test(key)) return i;
    }
    return NEW_PRODUCT_VALUE_ORDER.length;
  }
  function stableSortByRank(entries) {
    entries.forEach(function (e, i) { e.__origIdx = i; e.__rank = rankLabel(e.key); });
    entries.sort(function (a, b) {
      if (a.__rank !== b.__rank) return a.__rank - b.__rank;
      return a.__origIdx - b.__origIdx;
    });
  }
  function sortFieldsetRadios(fieldset) {
    var inputs = Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
    if (inputs.length < 2) return;
    var entries = inputs.map(function (input) {
      var label = null;
      if (input.id) {
        label = fieldset.querySelector('label[for="' + cssEscape(input.id) + '"]');
      }
      var labelText = (label && label.textContent) || input.value || '';
      return { input: input, label: label, key: labelText };
    });
    stableSortByRank(entries);
    entries.forEach(function (e) {
      fieldset.appendChild(e.input);
      if (e.label) fieldset.appendChild(e.label);
    });
  }
  function sortSelectOptions(select) {
    var options = Array.prototype.slice.call(select.options);
    if (options.length < 2) return;
    var entries = options.map(function (opt) {
      return { el: opt, key: opt.textContent || opt.value || '' };
    });
    stableSortByRank(entries);
    entries.forEach(function (e) { select.appendChild(e.el); });
  }
  function sortNewProductValues(pickers) {
    if (!document.body || !document.body.hasAttribute('new-product')) return;
    if (!pickers || !pickers.length) return;
    for (var i = 0; i < pickers.length; i++) {
      var picker = pickers[i];
      if (picker[SORTED_FLAG]) continue;
      var groups = picker.querySelectorAll(':scope > fieldset, :scope > .product-form__input--dropdown');
      for (var g = 0; g < groups.length; g++) {
        var el = groups[g];
        if (el.tagName === 'FIELDSET') {
          sortFieldsetRadios(el);
        } else {
          var sel = el.querySelector('select');
          if (sel) sortSelectOptions(sel);
        }
      }
      picker[SORTED_FLAG] = true;
    }
  }

  function setBusy(picker) {
    var host = picker.closest('product-info') || picker.parentElement;
    if (!host) return null;
    host.classList.add(BUSY_CLASS);
    // Safety net: auto-clear if no new picker arrives (e.g., the click didn't trigger a swap).
    if (host.__variantPickerBusyTimer) clearTimeout(host.__variantPickerBusyTimer);
    host.__variantPickerBusyTimer = setTimeout(function () {
      host.classList.remove(BUSY_CLASS);
      host.__variantPickerBusyTimer = null;
    }, BUSY_TIMEOUT_MS);
    return host;
  }

  function clearBusy(picker) {
    var host = picker.closest('product-info') || picker.parentElement;
    if (!host) return;
    host.classList.remove(BUSY_CLASS);
    if (host.__variantPickerBusyTimer) {
      clearTimeout(host.__variantPickerBusyTimer);
      host.__variantPickerBusyTimer = null;
    }
  }

  function scanAndSetup(reason) {
    var allPickers = document.querySelectorAll('variant-selects');
    sortNewProductValues(allPickers);
    var pickers = document.querySelectorAll('variant-selects[data-hide-dynamic="true"]');

    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, 'scan(' + reason + '): found', allPickers.length, '<variant-selects> total,', pickers.length, 'with data-hide-dynamic="true"');

    if (allPickers.length > 0 && pickers.length === 0) {
      // eslint-disable-next-line no-console
      console.log(LOG_PREFIX, 'scan: data-hide-dynamic values seen:',
        Array.prototype.map.call(allPickers, function (p) { return p.getAttribute('data-hide-dynamic'); }));
    }

    pickers.forEach(function (picker) {
      if (picker[SETUP_FLAG]) {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, 'scan: picker already set up, skipping', picker);
        return;
      }
      picker[SETUP_FLAG] = true;
      setupPicker(picker);
      // The fresh picker is now wired up — clear any "busy" spinner that was
      // set when the user clicked the option that triggered the swap.
      clearBusy(picker);
    });
  }

  function init() {
    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, 'init: scanning for <variant-selects data-hide-dynamic="true">');
    scanAndSetup('initial');

    // Watch for new pickers inserted by product-info.js section swaps.
    var observer = new MutationObserver(function (mutations) {
      var needsScan = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType !== 1) continue; // ELEMENT_NODE
          if (n.tagName === 'VARIANT-SELECTS' || n.querySelector && n.querySelector('variant-selects')) {
            needsScan = true;
            break;
          }
        }
        if (needsScan) break;
      }
      if (needsScan) {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, 'observer: new <variant-selects> inserted, re-scanning');
        scanAndSetup('mutation');
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Also re-scan after the existing variantChange pub/sub event as a belt-and-suspenders fallback.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        // The swap happens before this event fires, so defer one frame so the new node is in the DOM.
        requestAnimationFrame(function () { scanAndSetup('variantChange'); });
      });
    }
  }

  function setupPicker(picker) {
    var log = makeLogger(picker);
    // Unconditional setup log so we know setup ran:
    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, 'setupPicker: starting setup for', picker);

    var dataEl = picker.querySelector('script[data-product-variants]');
    if (!dataEl) {
      // eslint-disable-next-line no-console
      console.warn(LOG_PREFIX, 'setupPicker: ABORT — no <script data-product-variants> inside', picker);
      return;
    }
    log('setupPicker: found variants <script>', dataEl);

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(LOG_PREFIX, 'setupPicker: ABORT — failed to parse variants JSON', e, dataEl.textContent);
      return;
    }
    if (!Array.isArray(variants) || variants.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(LOG_PREFIX, 'setupPicker: ABORT — variants array empty or invalid', variants);
      return;
    }
    log('setupPicker: parsed', variants.length, 'variants:', variants);

    var groups = collectOptionGroups(picker, log);
    if (groups.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(LOG_PREFIX, 'setupPicker: ABORT — no option groups found in picker');
      return;
    }
    log('setupPicker: collected', groups.length, 'option groups:', groups);

    var refreshing = false;
    var refresh = function (source) {
      if (refreshing) {
        log('refresh: skipped (already refreshing)', 'source=', source);
        return;
      }
      refreshing = true;
      try {
        log('refresh: START', 'source=', source);
        refreshOptionVisibility(groups, variants, log);
        log('refresh: DONE');
      } finally {
        refreshing = false;
      }
    };

    refresh('initial');

    if (typeof subscribe === 'function') {
      log('setupPicker: subscribing to PUB_SUB event', EVENT_NAME);
      subscribe(EVENT_NAME, function (payload) {
        var target = payload && payload.data && payload.data.event && payload.data.event.target;
        if (target && picker.contains(target)) {
          log('pubsub: optionValueSelectionChange from this picker, target=', target);
          // Show spinner immediately — product-info will swap this picker out
          // for a fresh one in a moment, and the spinner stays on the stable
          // <product-info> parent until the new picker is wired up.
          setBusy(picker);
          refresh('pubsub');
        } else {
          log('pubsub: optionValueSelectionChange from OTHER picker, ignoring');
        }
      });
    } else {
      // eslint-disable-next-line no-console
      console.warn(LOG_PREFIX, 'setupPicker: subscribe() not defined — falling back to native change listener');
      picker.addEventListener('change', function () {
        setBusy(picker);
        refresh('change-event');
      });
    }
  }

  function collectOptionGroups(picker, log) {
    var groups = [];
    var children = picker.querySelectorAll(':scope > fieldset, :scope > .product-form__input--dropdown');
    log('collectOptionGroups: direct children matched =', children.length);

    children.forEach(function (el, index) {
      if (el.tagName === 'FIELDSET') {
        var inputs = Array.prototype.slice.call(el.querySelectorAll('input[type="radio"]'));
        if (!inputs.length) {
          log('collectOptionGroups: fieldset #' + index + ' has no radio inputs, skipping', el);
          return;
        }
        log('collectOptionGroups: fieldset #' + index + ' has', inputs.length, 'radio inputs', el);
        groups.push({ type: 'radio', index: index, container: el, inputs: inputs });
      } else {
        var select = el.querySelector('select');
        if (!select) {
          log('collectOptionGroups: dropdown #' + index + ' has no <select>, skipping', el);
          return;
        }
        var options = Array.prototype.slice.call(select.options);
        log('collectOptionGroups: dropdown #' + index + ' has', options.length, '<option>s', el);
        groups.push({ type: 'select', index: index, container: el, select: select, options: options });
      }
    });
    return groups;
  }

  function getSelectedValue(group) {
    if (group.type === 'radio') {
      var checked = group.inputs.find(function (i) { return i.checked; });
      return checked ? checked.value : null;
    }
    return group.select.value;
  }

  function isPrefixMatch(variant, prefixSelection) {
    for (var i = 0; i < prefixSelection.length; i++) {
      var sel = prefixSelection[i];
      if (sel == null) continue;
      if (!variant.options || variant.options[i] !== sel) return false;
    }
    return true;
  }

  function valueReachable(variants, prefixSelection) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.available && isPrefixMatch(v, prefixSelection)) return true;
    }
    return false;
  }

  function refreshOptionVisibility(groups, variants, log) {
    var current = groups.map(getSelectedValue);
    log('refreshOptionVisibility: current selection =', current);

    groups.forEach(function (group, gIndex) {
      var entries = group.type === 'radio'
        ? group.inputs.map(function (inp) { return { value: inp.value, el: inp }; })
        : group.options.map(function (opt) { return { value: opt.value, el: opt }; });

      log('  group[' + gIndex + '] type=' + group.type, 'values=', entries.map(function (e) { return e.value; }));

      var firstReachable = null;
      var evaluated = entries.map(function (entry) {
        var trial = current.slice(0, gIndex + 1);
        trial[gIndex] = entry.value;
        var reachable = valueReachable(variants, trial);
        if (reachable && firstReachable === null) firstReachable = entry;
        return { value: entry.value, el: entry.el, reachable: reachable, trial: trial };
      });

      log('  group[' + gIndex + '] reachability:', evaluated.map(function (e) {
        return e.value + (e.reachable ? '=YES' : '=NO');
      }).join(', '));

      var hideCount = 0;
      evaluated.forEach(function (entry) {
        if (!entry.reachable) hideCount++;
        applyVisibility(group, entry.el, !entry.reachable);
      });
      log('  group[' + gIndex + '] hide count =', hideCount, '/', evaluated.length);

      var selValue = current[gIndex];
      if (selValue != null) {
        var selectedEntry = evaluated.find(function (entry) { return entry.value === selValue; });
        if (selectedEntry && !selectedEntry.reachable && firstReachable) {
          log('  group[' + gIndex + '] selected value "' + selValue + '" is unreachable — auto-selecting "' + firstReachable.value + '"');
          autoSelect(group, firstReachable.el);
          current[gIndex] = firstReachable.value;
        } else if (selectedEntry && !selectedEntry.reachable && !firstReachable) {
          log('  group[' + gIndex + '] selected value "' + selValue + '" is unreachable but NO reachable alternative exists');
        }
      }
    });
  }

  function applyVisibility(group, el, hide) {
    if (group.type === 'radio') {
      el.classList.toggle(HIDDEN_CLASS, hide);
      if (el.id) {
        var label = group.container.querySelector('label[for="' + cssEscape(el.id) + '"]');
        if (label) label.classList.toggle(HIDDEN_CLASS, hide);
      }
    } else {
      if (hide) {
        el.setAttribute('hidden', 'hidden');
        el.disabled = true;
      } else {
        el.removeAttribute('hidden');
        el.disabled = false;
      }
    }
  }

  function autoSelect(group, targetEl) {
    if (group.type === 'radio') {
      targetEl.checked = true;
      targetEl.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      group.select.value = targetEl.value;
      var prev = Array.prototype.find.call(group.select.options, function (o) {
        return o.hasAttribute('selected');
      });
      if (prev) prev.removeAttribute('selected');
      targetEl.setAttribute('selected', 'selected');
      group.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function cssEscape(str) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(str);
    return String(str).replace(/[^a-zA-Z0-9_\-]/g, '\\$&');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Compact button — #open-{slug} link handler.
   When an <a class="compact-button"> link contains "#open-{slug}",
   intercept the click, find the matching product tab button (data-id
   match, then 'p_' prefix, then secondary class, then text-content
   substring), programmatically click it (which triggers the existing
   tab-content swap), and smooth-scroll to the tab container.

   Convention examples (for a tab <button class="product-tab--btn guides"
   data-id="p_guides">Our Process</button>):
   - #open-p_guides    → exact data-id match
   - #open-guides      → matches with 'p_' prefix OR via secondary class
   - #open-process     → matches via text-content substring ("Our Process")
*/
(function () {
  var PREFIX = '#open-';

  function findTabButton(slug) {
    var tabs = document.querySelectorAll('.product-tab--btn');
    if (!tabs.length) return null;
    var i;
    for (i = 0; i < tabs.length; i++) {
      if (tabs[i].dataset && tabs[i].dataset.id === slug) return tabs[i];
    }
    for (i = 0; i < tabs.length; i++) {
      if (tabs[i].dataset && tabs[i].dataset.id === 'p_' + slug) return tabs[i];
    }
    for (i = 0; i < tabs.length; i++) {
      if (tabs[i].classList.contains(slug)) return tabs[i];
    }
    var needle = slug.toLowerCase().replace(/[-_]+/g, ' ').trim();
    if (!needle) return null;
    for (i = 0; i < tabs.length; i++) {
      var text = (tabs[i].textContent || '').toLowerCase().trim();
      if (text.indexOf(needle) !== -1) return tabs[i];
    }
    return null;
  }

  function getHeaderOffset() {
    var header = document.querySelector('sticky-header, .header-wrapper');
    if (!header || typeof header.getBoundingClientRect !== 'function') return 0;
    var rect = header.getBoundingClientRect();
    // Only subtract when the header is actually overlapping the top of the viewport.
    if (rect.bottom > 0 && rect.top < window.innerHeight) {
      return rect.bottom;
    }
    return 0;
  }

  function scrollToTab(btn) {
    var target = btn.closest('.product-tab--btn-container') ||
                 btn.closest('.product-tab-container') ||
                 btn;
    if (!target || typeof target.getBoundingClientRect !== 'function') return;
    var tRect = target.getBoundingClientRect();
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    var targetY = tRect.top + scrollTop - getHeaderOffset() - 16;
    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  }

  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;
    var link = e.target.closest('a.compact-button');
    if (!link) return;
    var href = link.getAttribute('href') || '';
    var idx = href.indexOf(PREFIX);
    if (idx === -1) return;
    var slug = href.slice(idx + PREFIX.length).trim();
    if (!slug) return;
    var btn = findTabButton(slug);
    if (!btn) return;
    e.preventDefault();
    btn.click();
    scrollToTab(btn);
  }, false);
})();

/* Expandable-note ACK gate. Disables ATC + dynamic checkout + any
   .req_med_form element while any [data-ack-checkbox] from a required
   expandable_note is unchecked. Coexists with Shopify's own sold-out /
   quantity-rule disable: we only re-enable buttons that carry our own
   marker class, so Shopify's native disable is never overridden. */
(function () {
  var DISABLED_CLASS = 'product-form__submit--ack-blocked';
  var FORM_BLOCKED_CLASS = 'product-form--ack-blocked';
  var GATED_BUTTON_SELECTOR = '.product-form__submit, .req_med_form';

  function allValid() {
    var boxes = document.querySelectorAll('[data-ack-checkbox]');
    for (var i = 0; i < boxes.length; i++) {
      if (!boxes[i].checked) return false;
    }
    var inputs = document.querySelectorAll('[data-property-input][data-required="true"]');
    for (var j = 0; j < inputs.length; j++) {
      if (!(inputs[j].value || '').trim()) return false;
    }
    return true;
  }

  // The element that receives the hidden property mirror. The site's
  // custom Add-to-Cart flow reads inputs from this container when it
  // builds the cart payload, so the property only needs to live here.
  function findAtcTarget() {
    return document.querySelector('.expandable-note-props-container');
  }

  // Mirror property values into the ATC target as hidden inputs, gated on
  // the ACK checkbox state of the source's wrapper. Idempotent: diffs the
  // current set of [data-ack-property] mirrors against the desired set
  // and only mutates the DOM when something actually differs. Idempotency
  // is critical — without it, the MutationObserver on <product-info>
  // (which sees these mirrors) would re-fire on every evaluate() and
  // loop forever, and the target would have a brief mirror-absent window
  // on every sync (causing submits mid-window to drop the property).
  function syncProperties() {
    var target = findAtcTarget();
    if (!target) return;

    var desired = {};
    var sources = document.querySelectorAll('[data-property-name]');
    for (var j = 0; j < sources.length; j++) {
      var src = sources[j];
      var wrapper = src.closest('.expandable-note-wrapper');
      if (!wrapper) continue;
      var ack = wrapper.querySelector('[data-ack-checkbox]');
      if (!ack || !ack.checked) continue;
      var valueEl = src.querySelector('[data-property-input]');
      if (!valueEl) continue;
      var value = (valueEl.value || '').trim();
      if (!value) continue;
      var name = src.getAttribute('data-property-name') || '';
      if (!name) continue;
      // If the merchant accidentally entered the full Shopify wrapper
      // (e.g. "properties[Undertaking]" instead of just "Undertaking"),
      // unwrap it so we don't end up with "properties[properties[…]]".
      var unwrap = name.match(/^properties\[(.+)\]$/);
      if (unwrap) name = unwrap[1];
      desired['properties[' + name + ']'] = value;
    }

    var existing = target.querySelectorAll('input[data-ack-property]');
    for (var i = 0; i < existing.length; i++) {
      var input = existing[i];
      if (desired.hasOwnProperty(input.name) && desired[input.name] === input.value) {
        delete desired[input.name];
      } else {
        input.remove();
      }
    }

    for (var key in desired) {
      if (!desired.hasOwnProperty(key)) continue;
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = key;
      hidden.value = desired[key];
      hidden.setAttribute('data-ack-property', '');
      target.appendChild(hidden);
    }
  }

  function markInvalidBoxes() {
    var boxes = document.querySelectorAll('[data-ack-checkbox]');
    for (var i = 0; i < boxes.length; i++) {
      if (!boxes[i].checked) {
        var wrap = boxes[i].closest('.expandable-note__ack');
        if (wrap) wrap.classList.add('expandable-note__ack--invalid');
      }
    }
  }

  function evaluate() {
    var ok = allValid();
    var hint = null;
    if (!ok) {
      var hintSrc = document.querySelector('.expandable-note-wrapper[data-disabled-hint]');
      if (hintSrc) hint = hintSrc.getAttribute('data-disabled-hint');
    }
    var buttons = document.querySelectorAll(GATED_BUTTON_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (!ok) {
        if (!btn.disabled) {
          btn.disabled = true;
          btn.classList.add(DISABLED_CLASS);
          btn.setAttribute('aria-disabled', 'true');
        }
        if (hint) btn.setAttribute('data-ack-hint', hint);
        else btn.removeAttribute('data-ack-hint');
      } else {
        btn.removeAttribute('data-ack-hint');
        if (btn.classList.contains(DISABLED_CLASS)) {
          btn.disabled = false;
          btn.classList.remove(DISABLED_CLASS);
          btn.removeAttribute('aria-disabled');
        }
      }
    }
    var forms = document.querySelectorAll('form[data-type="add-to-cart-form"], product-form');
    for (var j = 0; j < forms.length; j++) {
      forms[j].classList.toggle(FORM_BLOCKED_CLASS, !ok);
    }
  }

  // Inject the property mirror at the moment the ATC button is clicked.
  // Re-runs over a few short delays so it still lands in the container
  // even if the popup template is inserted into the DOM asynchronously
  // after the click. syncProperties() is idempotent, so the extra calls
  // are cheap and never cause duplication.
  function scheduleSyncAroundClick() {
    syncProperties();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(syncProperties);
    }
    setTimeout(syncProperties, 50);
    setTimeout(syncProperties, 250);
    setTimeout(syncProperties, 600);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;
    var btn = e.target.closest('.req_med_btn');
    if (!btn) return;
    // Don't sync if the gate is going to block this click — the capture
    // handler above already handles that case.
    if (!allValid()) return;
    scheduleSyncAroundClick();
  }, false);

  document.addEventListener('change', function (e) {
    if (!e.target || typeof e.target.matches !== 'function') return;
    if (!e.target.matches('[data-ack-checkbox]')) return;
    var wrap = e.target.closest('.expandable-note__ack');
    if (wrap) wrap.classList.remove('expandable-note__ack--invalid');
    evaluate();
  });

  // Safety net: catch Enter-key submits and any race where the disabled
  // flag was bypassed. Capture phase runs before product-form.js handlers.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || typeof form.matches !== 'function') return;
    var isAtcForm = form.matches('form[data-type="add-to-cart-form"]') ||
                    form.matches('form[action="/cart/add"]') ||
                    form.matches('form[action^="/cart/add?"]');
    if (!isAtcForm) return;
    if (allValid()) return;
    e.preventDefault();
    e.stopPropagation();
    markInvalidBoxes();
  }, true);

  // Click intercept for .req_med_form — covers <a>/<div> elements where
  // the `disabled` attribute is a no-op, and pre-empts any custom click
  // handler attached to the element.
  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;
    var trigger = e.target.closest('.req_med_form');
    if (!trigger) return;
    if (allValid()) return;
    e.preventDefault();
    e.stopPropagation();
    markInvalidBoxes();
  }, true);

  // Re-evaluate (and re-sync hidden mirrors) as the customer types in a
  // visible property input.
  document.addEventListener('input', function (e) {
    if (!e.target || typeof e.target.matches !== 'function') return;
    if (!e.target.matches('[data-property-input]')) return;
    evaluate();
  });

  // Returns true if any added/removed node in the MutationRecord list is
  // something other than one of our own [data-ack-property] hidden mirrors.
  // Required because syncProperties() adds/removes those mirrors inside
  // <product-info>, which the observer is watching — without this filter,
  // each sync triggers another evaluate() → sync() → mutation → … loop.
  function hasMeaningfulMutation(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var a = 0; a < added.length; a++) {
        var n = added[a];
        if (n.nodeType !== 1) continue;
        if (n.hasAttribute && n.hasAttribute('data-ack-property')) continue;
        return true;
      }
      var removed = mutations[i].removedNodes;
      for (var r = 0; r < removed.length; r++) {
        var m = removed[r];
        if (m.nodeType !== 1) continue;
        if (m.hasAttribute && m.hasAttribute('data-ack-property')) continue;
        return true;
      }
    }
    return false;
  }

  function init() {
    evaluate();
    // Variant swap replaces the product-info subtree; re-evaluate on changes.
    var pinfo = document.querySelector('product-info');
    if (pinfo && typeof MutationObserver === 'function') {
      new MutationObserver(function (mutations) {
        if (hasMeaningfulMutation(mutations)) evaluate();
      }).observe(pinfo, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
